/**
 * Concept diagram for the merge-conflicts course intro.
 *
 * Tells the story in three panels: two branches edit the same line, git tries
 * to merge, and the result is a single file with conflict markers that the
 * human has to resolve.
 */
export function ConflictDiagram() {
  return (
    <div
      style={{
        background: "var(--bg-2)",
        border: "1px solid var(--border-1)",
        borderRadius: 14,
        padding: 22,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.12em",
          color: "var(--fg-2)",
        }}
      >
        How a conflict happens
      </div>

      <svg
        viewBox="0 0 560 280"
        style={{ width: "100%", height: 280, display: "block" }}
      >
        <defs>
          <marker
            id="conflict-arrow"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#D8443C" />
          </marker>
        </defs>

        {/* Two branches diverging from the same commit */}
        <line x1="20" y1="140" x2="120" y2="140" stroke="rgba(0,24,71,0.3)" strokeWidth="2" />
        <circle cx="60" cy="140" r="10" fill="#fff" stroke="#001847" strokeWidth="2" />
        <text x="60" y="168" textAnchor="middle" fontSize="10" fontFamily="ui-monospace, Menlo" fill="#001847">
          shared base
        </text>

        {/* Main branch goes up, edits H1 to "The Atlas …" */}
        <path
          d="M 120 140 C 160 140, 160 70, 210 70"
          stroke="#001847"
          strokeWidth="2"
          fill="none"
        />
        <circle cx="210" cy="70" r="11" fill="#fff" stroke="#001847" strokeWidth="2" />
        <text x="210" y="50" textAnchor="middle" fontSize="11" fontWeight="600" fill="#001847">
          you (main)
        </text>
        <foreignObject x="240" y="40" width="180" height="60">
          <div
            style={{
              fontFamily: "ui-monospace, Menlo",
              fontSize: 10.5,
              color: "#001847",
              background: "rgba(45,155,214,0.15)",
              border: "1px solid #2D9BD6",
              borderRadius: 6,
              padding: "6px 8px",
              lineHeight: 1.4,
            }}
          >
            &lt;h1&gt;The Atlas operating system&lt;/h1&gt;
          </div>
        </foreignObject>

        {/* Feature branch goes down, edits H1 to "Atlas — your team's BOS" */}
        <path
          d="M 120 140 C 160 140, 160 210, 210 210"
          stroke="#7B4FB8"
          strokeWidth="2"
          fill="none"
        />
        <circle cx="210" cy="210" r="11" fill="#7B4FB8" stroke="#001847" strokeWidth="2" />
        <text x="210" y="234" textAnchor="middle" fontSize="11" fontWeight="600" fill="#7B4FB8">
          teammate (feature)
        </text>
        <foreignObject x="240" y="190" width="180" height="60">
          <div
            style={{
              fontFamily: "ui-monospace, Menlo",
              fontSize: 10.5,
              color: "#001847",
              background: "rgba(123,79,184,0.12)",
              border: "1px solid #7B4FB8",
              borderRadius: 6,
              padding: "6px 8px",
              lineHeight: 1.4,
            }}
          >
            &lt;h1&gt;Atlas — your team's BOS&lt;/h1&gt;
          </div>
        </foreignObject>

        {/* The merge arrow + ! */}
        <path
          d="M 420 70 C 470 70, 470 140, 500 140"
          stroke="#D8443C"
          strokeWidth="2"
          strokeDasharray="5 4"
          fill="none"
          markerEnd="url(#conflict-arrow)"
        />
        <path
          d="M 420 210 C 470 210, 470 140, 500 140"
          stroke="#D8443C"
          strokeWidth="2"
          strokeDasharray="5 4"
          fill="none"
          markerEnd="url(#conflict-arrow)"
        />
        <circle cx="510" cy="140" r="16" fill="#D8443C" stroke="#fff" strokeWidth="2" />
        <text x="510" y="146" textAnchor="middle" fontSize="20" fontWeight="700" fill="#fff">
          !
        </text>
        <text x="510" y="180" textAnchor="middle" fontSize="11" fontWeight="700" fill="#D8443C">
          conflict
        </text>
      </svg>

      <div
        style={{
          fontFamily: "ui-monospace, Menlo",
          fontSize: 11,
          background: "#001847",
          color: "#fff",
          padding: "12px 14px",
          borderRadius: 8,
          lineHeight: 1.6,
          whiteSpace: "pre",
          overflow: "auto",
        }}
      >
        <span style={{ color: "#FFC857" }}>{"<<<<<<< HEAD (main)"}</span>
        {"\n  <h1>The Atlas operating system</h1>\n"}
        <span style={{ color: "#A0A4B8" }}>{"======="}</span>
        {"\n  <h1>Atlas — your team's BOS</h1>\n"}
        <span style={{ color: "#7BB9F0" }}>{">>>>>>> feature"}</span>
      </div>

      <div style={{ fontSize: 12, color: "var(--fg-3)" }}>
        Git doesn't guess. It writes both versions into the file and waits for
        a human to pick.
      </div>
    </div>
  );
}
